import React, { useCallback } from "react";
import { View, Text, StyleSheet, Platform } from "react-native";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../store";
import LHSectionHeader from "../../components/LHSectionHeader";
import SignaturePad from "../../components/SignaturePad";
import {
  resetManagerSignatureFlag,
  setManagerSignature,
} from "../../store/slices/sparcWalkInspectionSlice";
import FontFamily from "../../constants/fonts";
import { useTheme } from "../../theme";

interface SparcWalkSignatureProps {
  isDisabled?: boolean;
  error?: boolean;
  errorMessage?: string;
}

const SparcWalkSignature: React.FC<SparcWalkSignatureProps> = ({
  isDisabled: propsIsDisabled,
  error = false,
  errorMessage = "",
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const statusLabelColor = isDark ? colors.text.inverse : "#4B4B4B";
  const dispatch = useDispatch();
  const { managerSignatureFileName, managerSignatureImageUrl, walkState } =
    useSelector((state: RootState) => state.sparcWalkInspection);

  // If isDisabled is provided as a prop, use it, otherwise check if walkState is completed
  const isDisabled =
    propsIsDisabled !== undefined ? propsIsDisabled : walkState === "completed";

  const showSignatureUrl =
    managerSignatureFileName || managerSignatureImageUrl
      ? managerSignatureImageUrl || managerSignatureFileName
      : undefined;

  console.log("SparcWalkSignature: signature data from state:", {
    managerSignatureFileName,
    managerSignatureImageUrl,
    showSignatureUrl,
  });

  // Function to handle clearing signature data from Redux
  const handleClearSignature = useCallback(() => {
    console.log("SparcWalkSignature: Clearing signature data from Redux");
    dispatch(
      setManagerSignature({
        fileName: null,
        imageUrl: null,
        isNewUpload: false,
      })
    );
    dispatch(resetManagerSignatureFlag());
  }, [dispatch]);

  return (
    <View
      style={[
        styles.sectionContainer,
        isDark && {
          backgroundColor: colors.background.surface,
          borderColor: colors.border.default,
          borderWidth: 1,
        },
      ]}
    >
      <LHSectionHeader
        title="Manager Signature"
        containerStyle={{ ...styles.header, backgroundColor: colors.brand.primary }}
        textStyle={styles.sectionHeaderText}
      />

      {/* Single Signature Pad Container */}
      <View style={{ backgroundColor: isDark ? colors.background.surface : "white" }}>
        <View
          style={[
            styles.signaturePadWrapper,
            isDark && { backgroundColor: colors.background.surface },
          ]}
        >
          <View style={styles.signaturePadContainer}>
            <SignaturePad
              containerName="sparc"
              isCompleted={walkState === "completed"}
              isDarkTheme={isDark}
              surfaceColor={isDark ? colors.background.page : colors.background.surface}
              elevatedColor={isDark ? colors.background.surface : colors.background.elevated}
              borderColor={colors.border.default}
              textColor={isDark ? colors.text.inverse : colors.text.primary}
              onOK={(fileName: string, imageUrl?: string) => {
                console.log(
                  "SparcWalkSignature: Received signature data - fileName:",
                  fileName,
                  "imageUrl:",
                  imageUrl
                );
                dispatch(
                  setManagerSignature({
                    fileName,
                    imageUrl,
                    isNewUpload: true,
                  })
                );
              }}
              onClear={handleClearSignature}
              initialImageUrl={showSignatureUrl || undefined}
              disabled={isDisabled}
            />
            {(managerSignatureFileName || managerSignatureImageUrl) && (
              <Text style={[styles.signatureText, isDark && { color: colors.text.primary }]}> 
                Manager Signature uploaded
              </Text>
            )}
            {error && <Text style={[styles.errorText, isDark && { color: colors.status.error }]}>{errorMessage}</Text>}
          </View>
        </View>

        {/* Status Summary */}
        <View
          style={[
            styles.statusContainer,
            isDark && {
              backgroundColor: colors.background.surface,
              borderTopColor: colors.border.default,
            },
          ]}
        >
          <View style={{ alignItems: "center" }}>
            <View style={styles.statusRow}>
              <Text style={[styles.statusLabel, { color: statusLabelColor }]}>Manager:</Text>
              <Text
                style={[
                  styles.statusValue,
                  managerSignatureFileName || managerSignatureImageUrl
                    ? styles.statusComplete
                    : styles.statusPending,
                ]}
              >
                {managerSignatureFileName || managerSignatureImageUrl
                  ? "Signed"
                  : "Pending"}
              </Text>
            </View>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  sectionContainer: {
    //marginBottom: 24,
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    // allow header to expand beyond original bounds when wrapping
    overflow: Platform.OS === "ios" ? "visible" : "hidden",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
    marginTop: 24,
  },
  header: {
    backgroundColor: "#3582E6",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    // use minHeight so header can expand to 2 lines when needed
    minHeight: 48,
  },
  sectionHeaderText: {
    fontSize: 14,
    fontWeight: "700",
    lineHeight: 20,
    fontFamily: FontFamily.bold,
  },
  signaturePadWrapper: {
    marginBottom: 0,
    backgroundColor: "white",
    padding: 16,
  },
  signaturePadContainer: {
    padding: 10,
  },
  signatureText: {
    marginTop: 8,
    fontSize: 14,
    color: "#2D2D2D",
    fontWeight: "500",
    textAlign: "center",
    fontFamily: FontFamily.regular,
  },
  errorText: {
    marginTop: 8,
    fontSize: 14,
    color: "red",
    fontWeight: "500",
    textAlign: "center",
    fontFamily: FontFamily.regular,
  },
  statusContainer: {
    backgroundColor: "white",
    borderRadius: 6,
    padding: 12,
    marginTop: 20,
    borderTopWidth: 8,
    borderTopColor: "#f5f7fa",
    paddingTop: 16,
  },
  statusRow: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    marginBottom: 6,
    width: "96%",
    marginLeft: 20,
  },
  statusLabel: {
    fontSize: 14,
    fontWeight: "500",
    color: "#FFFFFF",
    width: 100,
    fontFamily: FontFamily.regular,
  },
  statusValue: {
    fontSize: 14,
    fontWeight: "600",
    fontFamily: FontFamily.regular,
  },
  statusComplete: {
    color: "#28a745",
  },
  statusPending: {
    color: "#ffc107",
  },
});

export default SparcWalkSignature;
